import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { LoginRecoveryService } from '@pages/login-recovery-options/services/login-recovery.service';
import {
  RecoverCredentialsByCompany,
  RecoverCredentialsByPassword,
  RecoverCredentialsByUsername,
} from '@pages/login-recovery-options/store/actions/login-recovery.actions';
import { LoginRecoveryPath } from '@pages/login-recovery-options/routes/login-recovery-path.enum';
import { ApplicationPath } from '@core/navigation/models/application-path.enum';
import { Router } from '@angular/router';
import { LoginRecoveryCredentials } from '@pages/login-recovery-options/models/login-recovery-credentials.type';
import { LoginRecoveryOption } from '@pages/login-recovery-options/models/login-recovery-option.enum';
import { Observable, catchError, finalize, tap, throwError } from 'rxjs';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';

interface LoginRecoveryStateModel {
  loading: boolean;
  recoveryCredentials?: LoginRecoveryCredentials;
  recoveryOption?: LoginRecoveryOption;
  errorMessageTranslationKey?: string;
}

@State<LoginRecoveryStateModel>({
  name: 'loginRecovery',
  defaults: {
    loading: false,
  },
})
@Injectable()
export class LoginRecoveryState {
  private readonly loginRecoveryService = inject(LoginRecoveryService);
  private readonly router = inject(Router);

  @Selector()
  static loading(state: LoginRecoveryStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static recoveryCredentials(state: LoginRecoveryStateModel): LoginRecoveryCredentials | undefined {
    return state.recoveryCredentials;
  }

  @Selector()
  static recoveryOption(state: LoginRecoveryStateModel): LoginRecoveryOption | undefined {
    return state.recoveryOption;
  }

  @Selector()
  static errorMessageTranslationKey(state: LoginRecoveryStateModel): string | undefined {
    return state.errorMessageTranslationKey;
  }

  @Action(RecoverCredentialsByUsername)
  recoveryCredentialsByUsername(
    context: StateContext<LoginRecoveryStateModel>,
    { recoverUsernameCredentials }: RecoverCredentialsByUsername
  ): Observable<void> {
    context.patchState({ loading: true });

    return this.loginRecoveryService.recoverCredentialsByUsername(recoverUsernameCredentials).pipe(
      tap(() => this.completeRecovery(context, recoverUsernameCredentials, LoginRecoveryOption.Username)),
      finalize(() => context.patchState({ loading: false }))
    );
  }

  @Action(RecoverCredentialsByCompany)
  recoveryCredentialsByCompany(
    context: StateContext<LoginRecoveryStateModel>,
    { recoverCompanyCredentials }: RecoverCredentialsByCompany
  ): Observable<void> {
    context.patchState({ loading: true });

    return this.loginRecoveryService.recoverCredentialsByCompanyName(recoverCompanyCredentials).pipe(
      tap(() => this.completeRecovery(context, recoverCompanyCredentials, LoginRecoveryOption.CompanyName)),
      catchError((error: HttpErrorResponse) => {
        if (error.status === HttpStatusCode.TooManyRequests) {
          context.patchState({ errorMessageTranslationKey: 'login.recovery.by-company-name.too-many-requests-error' });
        }

        return throwError(() => error);
      }),
      finalize(() => context.patchState({ loading: false }))
    );
  }

  @Action(RecoverCredentialsByPassword)
  recoveryCredentialsByPassword(
    context: StateContext<LoginRecoveryStateModel>,
    { recoverPasswordCredentials }: RecoverCredentialsByPassword
  ): Observable<void> {
    context.patchState({ loading: true });

    return this.loginRecoveryService.recoverCredentialsByPassword(recoverPasswordCredentials).pipe(
      tap(() => this.completeRecovery(context, recoverPasswordCredentials, LoginRecoveryOption.Password)),
      finalize(() => context.patchState({ loading: false }))
    );
  }

  private completeRecovery(
    context: StateContext<LoginRecoveryStateModel>,
    recoveryCredentials: LoginRecoveryCredentials,
    recoveryOption: LoginRecoveryOption
  ): void {
    context.patchState({ loading: false, recoveryCredentials, recoveryOption });
    this.router.navigateByUrl(`${ApplicationPath.CannotLogin}/${LoginRecoveryPath.EmailSent}`);
  }
}
